/**
 * Healthcare service entitlement helpers — platform catalog + provider allowlist.
 */
import type { HealthcareService, List, PractitionerRole, Reference } from '@medplum/fhirtypes';
import {
  ENABLED_HEALTHCARE_SERVICES_LIST_CODE,
  LIST_TYPE_SYSTEM,
  PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM,
  PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_VALUE,
  PRACTICE_SERVICES_LIST_IDENTIFIER_SYSTEM,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════
//  Platform catalog (HealthcareService)
// ═══════════════════════════════════════════════════════════════════════

export function isPlatformCatalogHealthcareService(svc: HealthcareService): boolean {
  return (
    svc.identifier?.some(
      (id) =>
        id.system === PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM
        && id.value === PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_VALUE,
    ) ?? false
  );
}

export function markHealthcareServiceAsPlatformCatalog(svc: HealthcareService): HealthcareService {
  const identifiers = [...(svc.identifier ?? [])].filter(
    (id) => id.system !== PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM,
  );
  identifiers.push({
    system: PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM,
    value: PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_VALUE,
  });
  const { providedBy: _removed, ...rest } = svc;
  return { ...rest, identifier: identifiers };
}

export function healthcareServiceReference(serviceId: string): Reference<HealthcareService> {
  return { reference: `HealthcareService/${serviceId}` };
}

export function parseHealthcareServiceId(ref: Reference | undefined): string | undefined {
  const reference = ref?.reference;
  if (!reference?.startsWith('HealthcareService/')) return undefined;
  return reference.slice('HealthcareService/'.length);
}

// ═══════════════════════════════════════════════════════════════════════
//  Practice enablement (List) — mirrors medplum-ui healthcare-service-entitlement
// ═══════════════════════════════════════════════════════════════════════

export function isPracticeEnabledServicesList(list: List): boolean {
  return (
    list.code?.coding?.some(
      (c) => c.system === LIST_TYPE_SYSTEM && c.code === ENABLED_HEALTHCARE_SERVICES_LIST_CODE,
    ) ?? false
  );
}

export function getEnabledHealthcareServiceIdsFromList(list: List): string[] {
  const ids: string[] = [];
  for (const entry of list.entry ?? []) {
    const id = parseHealthcareServiceId(entry.item);
    if (id) ids.push(id);
  }
  return ids;
}

export function buildPracticeEnabledServicesList(
  practiceOrgId: string,
  serviceIds: string[],
  existing?: List,
): List {
  return {
    resourceType: 'List',
    id: existing?.id,
    status: 'current',
    mode: 'working',
    title: 'Enabled healthcare services',
    code: {
      coding: [
        {
          system: LIST_TYPE_SYSTEM,
          code: ENABLED_HEALTHCARE_SERVICES_LIST_CODE,
          display: 'Enabled session types',
        },
      ],
    },
    identifier: [
      ...(existing?.identifier?.filter((i) => i.system !== PRACTICE_SERVICES_LIST_IDENTIFIER_SYSTEM) ?? []),
      { system: PRACTICE_SERVICES_LIST_IDENTIFIER_SYSTEM, value: practiceOrgId },
    ],
    subject: { reference: `Organization/${practiceOrgId}` },
    entry: serviceIds.map((id) => ({
      item: healthcareServiceReference(id),
    })),
  };
}

/**
 * Effective schedulable service ids = platform catalog ids that are also in
 * both the practice-enabled set and the provider allowlist.
 */
export function computeEffectiveHealthcareServiceIds(args: {
  platformCatalog: HealthcareService[];
  practiceEnabledIds: string[];
  providerAllowlistIds: string[];
}): string[] {
  const practice = new Set(args.practiceEnabledIds);
  const provider = new Set(args.providerAllowlistIds);
  return args.platformCatalog
    .map((hs) => hs.id)
    .filter((id): id is string => Boolean(id) && practice.has(id!) && provider.has(id!));
}

// ═══════════════════════════════════════════════════════════════════════
//  Provider allowlist (PractitionerRole.healthcareService)
// ═══════════════════════════════════════════════════════════════════════

export function getProviderHealthcareServiceIds(role: PractitionerRole): string[] {
  const ids: string[] = [];
  for (const ref of role.healthcareService ?? []) {
    const id = parseHealthcareServiceId(ref);
    if (id) ids.push(id);
  }
  return ids;
}

export function setProviderHealthcareServiceIds(
  role: PractitionerRole,
  serviceIds: string[],
): PractitionerRole {
  return {
    ...role,
    healthcareService: serviceIds.map((id) => healthcareServiceReference(id)),
  };
}
